import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/** Custom date / datetime / time picker — replaces native <input type=date|datetime-local|time>
 *  (whose popup calendar is OS-drawn and can't be themed). Rounded popup, today + selected day
 *  highlighted in the theme accent, rounded day cells, themed time fields. Values stay in the
 *  same local strings the native inputs use: 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm', 'HH:mm'. */

type Mode = 'date' | 'datetime' | 'time';
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WD = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const pad = (n: number) => String(n).padStart(2, '0');
type Parts = { y: number; mo: number; d: number; hh: number; mm: number };

function parse(value: string, mode: Mode): Parts | null {
  if (!value) return null;
  if (mode === 'time') {
    const m = /^(\d{1,2}):(\d{2})/.exec(value);
    if (!m) return null;
    return { y: 0, mo: 0, d: 0, hh: +m[1], mm: +m[2] };
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{1,2}):(\d{2}))?/.exec(value);
  if (!m) return null;
  return { y: +m[1], mo: +m[2] - 1, d: +m[3], hh: m[4] ? +m[4] : 0, mm: m[5] ? +m[5] : 0 };
}
function toValue(p: Parts, mode: Mode): string {
  if (mode === 'time') return `${pad(p.hh)}:${pad(p.mm)}`;
  const date = `${p.y}-${pad(p.mo + 1)}-${pad(p.d)}`;
  return mode === 'datetime' ? `${date}T${pad(p.hh)}:${pad(p.mm)}` : date;
}
function display(value: string, mode: Mode, placeholder?: string): string {
  const p = parse(value, mode);
  if (!p) return placeholder || (mode === 'time' ? '--:--' : 'Pick a date');
  if (mode === 'time') return `${pad(p.hh)}:${pad(p.mm)}`;
  const base = `${p.d} ${MON[p.mo]} ${p.y}`;
  return mode === 'datetime' ? `${base}, ${pad(p.hh)}:${pad(p.mm)}` : base;
}

export function DateTimePicker({
  value, onChange, mode = 'datetime', style, className, placeholder, ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  mode?: Mode;
  style?: React.CSSProperties;
  className?: string;
  placeholder?: string;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  const now = new Date();
  const cur = parse(value, mode);
  const [viewY, setViewY] = useState(cur?.y || now.getFullYear());
  const [viewMo, setViewMo] = useState(cur ? cur.mo : now.getMonth());
  useEffect(() => { if (cur && open) { setViewY(cur.y); setViewMo(cur.mo); } /* eslint-disable-next-line */ }, [open]);

  const place = () => {
    const b = wrapRef.current?.getBoundingClientRect();
    if (!b) return;
    const below = window.innerHeight - b.bottom;
    setPos({ left: Math.min(b.left, window.innerWidth - 300), top: below < 340 && b.top > 340 ? b.top - 8 : b.bottom + 4 });
  };
  useLayoutEffect(() => { if (open) place(); }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!wrapRef.current?.contains(t) && !menuRef.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); window.removeEventListener('resize', place); };
  }, [open]);

  const emit = (next: Partial<Parts>) => {
    const base: Parts = cur || { y: now.getFullYear(), mo: now.getMonth(), d: now.getDate(), hh: 9, mm: 0 };
    onChange(toValue({ ...base, ...next }, mode));
  };
  const pickDay = (d: number) => {
    emit({ y: viewY, mo: viewMo, d });
    if (mode === 'date') setOpen(false);
  };
  const setTime = (hh: number, mm: number) => emit({ hh: Math.max(0, Math.min(23, hh)), mm: Math.max(0, Math.min(59, mm)) });

  // Month grid, Monday-first.
  const first = (new Date(viewY, viewMo, 1).getDay() + 6) % 7;
  const days = new Date(viewY, viewMo + 1, 0).getDate();
  const cells: (number | null)[] = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  const isToday = (d: number) => viewY === now.getFullYear() && viewMo === now.getMonth() && d === now.getDate();
  const isSel = (d: number) => !!cur && cur.y === viewY && cur.mo === viewMo && cur.d === d;
  const nav = (delta: number) => { const m = viewMo + delta; setViewY(viewY + Math.floor(m / 12)); setViewMo(((m % 12) + 12) % 12); };

  return (
    <div className={`fx-dtp${className ? ` ${className}` : ''}`} ref={wrapRef} style={style}>
      <button type="button" className="fx-dtp-btn" aria-label={ariaLabel} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className={`fx-dtp-val${cur ? '' : ' fx-select-ph'}`}>{display(value, mode, placeholder)}</span>
        <svg className="fx-select-chev" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          {mode === 'time'
            ? <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>
            : <><rect x="3" y="4.5" width="18" height="17" rx="2.5" /><path d="M3 9h18M8 2v4M16 2v4" /></>}
        </svg>
      </button>
      {open && pos && createPortal(
        <div className="fx-dtp-pop" ref={menuRef} role="dialog" style={{ position: 'fixed', left: pos.left, top: pos.top }}>
          {mode !== 'time' && (
            <>
              <div className="fx-dtp-head">
                <button type="button" className="fx-dtp-nav" onClick={() => nav(-1)} aria-label="Previous month">‹</button>
                <span className="fx-dtp-title">{MON[viewMo]} {viewY}</span>
                <button type="button" className="fx-dtp-nav" onClick={() => nav(1)} aria-label="Next month">›</button>
              </div>
              <div className="fx-dtp-grid fx-dtp-wd">{WD.map((w) => <span key={w} className="fx-dtp-wdc">{w}</span>)}</div>
              <div className="fx-dtp-grid">
                {cells.map((d, i) => d === null
                  ? <span key={`b${i}`} />
                  : <button key={d} type="button" className={`fx-dtp-day${isSel(d) ? ' is-sel' : ''}${isToday(d) ? ' is-today' : ''}`} onClick={() => pickDay(d)}>{d}</button>)}
              </div>
            </>
          )}
          {mode !== 'date' && (
            <div className="fx-dtp-time">
              <span className="fx-dtp-time-lbl">Time</span>
              <input className="fx-dtp-num" type="number" min={0} max={23} value={cur ? pad(cur.hh) : '09'} onChange={(e) => setTime(+e.target.value || 0, cur?.mm ?? 0)} aria-label="Hour" />
              <span className="fx-dtp-colon">:</span>
              <input className="fx-dtp-num" type="number" min={0} max={59} value={cur ? pad(cur.mm) : '00'} onChange={(e) => setTime(cur?.hh ?? 9, +e.target.value || 0)} aria-label="Minute" />
              {mode === 'time' && <button type="button" className="fx-dtp-done" onClick={() => setOpen(false)}>Done</button>}
            </div>
          )}
          {mode !== 'time' && (
            <div className="fx-dtp-foot">
              <button type="button" className="fx-dtp-link" onClick={() => { setViewY(now.getFullYear()); setViewMo(now.getMonth()); pickDay(now.getDate()); }}>Today</button>
              <button type="button" className="fx-dtp-link" onClick={() => { onChange(''); setOpen(false); }}>Clear</button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
